import { ReactComponent as FilterClearIcon } from "assets/icons/filter-clear.svg";
import { useGetProfessionalAreasAsItems } from "modules/codebooks";
import { FilterGroup, useOutsideClick } from "modules/common";
import {
    useGetFilterPreferencesQuery,
    useUpdateFilterPreferences,
} from "modules/common/hooks/filterPreferencesHooks";
import { handleKeyDown } from "modules/common/utils/keyboardUtils";
import { DocumentType, documentTypeFilterFields } from "modules/educationDocuments/constants";
import React, { useEffect, useRef, useState } from "react";
import { GenericSwitchFilter } from "../../common/components/GenericSwitchFilter/GenericSwitchFilter";

interface Props {
    professionalAreaFilter: string[];
    setProfessionalAreaFilter: React.Dispatch<React.SetStateAction<string[]>>;
    documentTypeFilter: DocumentType[];
    setDocumentTypeFilter: React.Dispatch<React.SetStateAction<DocumentType[]>>;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

export const EducationDocumentsFilters: React.FC<Props> = ({
    professionalAreaFilter,
    setProfessionalAreaFilter,
    documentTypeFilter,
    setDocumentTypeFilter,
    setCurrentPage,
}) => {
    const PROFESSIONAL_AREA_FILTER_NAME = `${EducationDocumentsFilters.name}-professionalAreaFilter`;
    const DOCUMENT_TYPE_FILTER_NAME = `${EducationDocumentsFilters.name}-documentTypeFilter`;

    const filterPreferencesQuery = useGetFilterPreferencesQuery([
        PROFESSIONAL_AREA_FILTER_NAME,
        DOCUMENT_TYPE_FILTER_NAME,
    ]);

    const updateFilterPreferencesMutation = useUpdateFilterPreferences();

    const [showProfessionalAreaFilter, setShowProfessionalAreaFilter] = useState(false);
    const [showDocumentTypeFilter, setShowDocumentTypeFilter] = useState(false);

    const professionalAreaFilterRef = useRef<HTMLDivElement>(null);
    const documentTypeFilterRef = useRef<HTMLDivElement>(null);

    const isProfessionalAreaFilterActive = professionalAreaFilter.length > 0;
    const isDocumentTypeFilterActive = documentTypeFilter.length > 0;

    const handleProfessionalAreaFilterOutsideClick = () => {
        setShowProfessionalAreaFilter(false);
    };

    const handleDocumentTypeFilterOutsideClick = () => {
        setShowDocumentTypeFilter(false);
    };

    useOutsideClick(professionalAreaFilterRef, handleProfessionalAreaFilterOutsideClick);
    useOutsideClick(documentTypeFilterRef, handleDocumentTypeFilterOutsideClick);

    const handleProfessionalAreaFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setProfessionalAreaFilter(prev => {
            const newValues = prev.includes(value)
                ? prev.filter(s => s !== value)
                : [...prev, value];
            handleUpdateFilterPreferences(PROFESSIONAL_AREA_FILTER_NAME, value);

            return newValues;
        });
    };

    const handleDocumentTypeFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const documentTypeValue = Number(value);

        setDocumentTypeFilter(prev => {
            const newValues = prev.includes(documentTypeValue as DocumentType)
                ? prev.filter(s => s !== documentTypeValue)
                : [...prev, documentTypeValue as DocumentType];

            handleUpdateFilterPreferences(DOCUMENT_TYPE_FILTER_NAME, documentTypeValue);
            return newValues;
        });
    };

    const handleProfessionalAreaFilterClear = () => {
        updateFilterPreferencesMutation.mutate({
            filterName: PROFESSIONAL_AREA_FILTER_NAME,
            clearFilter: true,
        });
        setProfessionalAreaFilter([]);
    };

    const handleDocumentTypeFilterClear = () => {
        updateFilterPreferencesMutation.mutate({
            filterName: DOCUMENT_TYPE_FILTER_NAME,
            clearFilter: true,
        });
        setDocumentTypeFilter([]);
    };

    const handleProfessionalAreaFilterClick = () => {
        setShowProfessionalAreaFilter(prev => !prev);
    };

    const handleDocumentTypeFilterClick = () => {
        setShowDocumentTypeFilter(prev => !prev);
    };

    const handleUpdateFilterPreferences = (filterName: string, filterValue: number | string) => {
        if (typeof filterValue === "string") {
            updateFilterPreferencesMutation.mutate({
                filterName,
                filterStringValue: filterValue,
            });
        } else {
            updateFilterPreferencesMutation.mutate({
                filterName,
                filterIntValue: filterValue,
            });
        }
        setCurrentPage(1);
    };

    const handleClearFilter = () => {
        handleProfessionalAreaFilterClear();
        handleDocumentTypeFilterClear();
    };

    const professionalAreasAsItems = useGetProfessionalAreasAsItems();

    const documentTypeOptions = Object.values(documentTypeFilterFields).map(({ value, label }) => ({
        key: value.toString(),
        value: label,
    }));

    useEffect(() => {
        const isLoading = filterPreferencesQuery.some(query => query.isLoading);
        if (isLoading) return;

        const professionalAreaPreference = filterPreferencesQuery.find(
            query => query.data?.filterName === PROFESSIONAL_AREA_FILTER_NAME
        );

        const documentTypePreference = filterPreferencesQuery.find(
            query => query.data?.filterName === DOCUMENT_TYPE_FILTER_NAME
        );

        const professionalAreaValues = professionalAreaPreference?.data?.filterStringValues;
        if (professionalAreaValues?.length) {
            setProfessionalAreaFilter(professionalAreaValues);
        }

        const documentTypeValues = documentTypePreference?.data?.filterIntValues;
        if (documentTypeValues?.length) {
            const documentTypeValuesProcessed = documentTypeValues.map(
                value => value as DocumentType
            );
            setDocumentTypeFilter(documentTypeValuesProcessed);
        }
    }, [filterPreferencesQuery.some(q => q.isLoading)]);

    return (
        <FilterGroup text="">
            <div className="mr-2 documents-filter" ref={professionalAreaFilterRef}>
                <GenericSwitchFilter
                    name="STRUČNO PODRUČJE"
                    onChange={handleProfessionalAreaFilterChange}
                    onClear={handleProfessionalAreaFilterClear}
                    onClick={handleProfessionalAreaFilterClick}
                    values={professionalAreaFilter}
                    isActive={isProfessionalAreaFilterActive}
                    showDropdown={showProfessionalAreaFilter}
                    list={professionalAreasAsItems.map(item => ({
                        key: item.guid,
                        value: item.label,
                    }))}
                />
            </div>

            <div className="mr-2 documents-filter" ref={documentTypeFilterRef}>
                <GenericSwitchFilter
                    name="TIP DOKUMENTA"
                    onChange={handleDocumentTypeFilterChange}
                    onClear={handleDocumentTypeFilterClear}
                    onClick={handleDocumentTypeFilterClick}
                    values={documentTypeFilter.map(dt => dt.toString())}
                    isActive={isDocumentTypeFilterActive}
                    showDropdown={showDocumentTypeFilter}
                    list={documentTypeOptions}
                />
            </div>

            {(professionalAreaFilter.length > 0 || documentTypeFilter.length > 0) && (
                <FilterClearIcon
                    className="filter-group__icon clear-filter"
                    onClick={handleClearFilter}
                    tabIndex={0}
                    onKeyDown={e => handleKeyDown(e, handleClearFilter)}
                />
            )}
        </FilterGroup>
    );
};
